import React, { useEffect, useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ENVIRONMENT_TYPES } from "Constants/constants";
import { BackLink, Button, ButtonWrapper, IconControl } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Dialog from "ds/Dialog";
import Input from "ds/Input";
import Loading from "ds/PageLoading";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { isValidEmail } from "Libs/validators";
import { selectEmailsWithProjectAccessOrInvitationSent } from "Reducers/access/access.selectors";
import { clearInvitationErrors } from "Reducers/access/invitation/invitation.reducer";
import {
  statusSelector,
  invitationErrorsSelector
} from "Reducers/access/invitation/invitation.selectors";
import { addInvitationThunk } from "Reducers/access/invitation/thunks/addInvitation.thunk";
import {
  selectFeatureAddProjectUserPricingAgreementEnabled,
  selectProjectAccessSelectEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { useLoggedInMember } from "Reducers/organization/loggedInMember/hooks/useLoggedInMember";
import { selectHasManagementAccess } from "Reducers/project/project";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import EnvironmentTypeRow from "../Common/EnvironmentTypeRow";

import * as S from "./InvitationCreate.styles";
import { OrganizationMemberSelect } from "./OrganizationMemberSelect/OrganizationMemberSelect";

import type { ConsoleAccessRole } from "@packages/client";

export const InvitationCreate = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const existingEmails = useSelectorWithUrlParams(
    selectEmailsWithProjectAccessOrInvitationSent
  );
  const selectEnabled = useAppSelector(selectProjectAccessSelectEnabled);
  const addProjectUserPricingAgreementEnabled = useAppSelector(
    selectFeatureAddProjectUserPricingAgreementEnabled
  );
  const [email, setEmail] = useState<string>("");
  const [validEmail, setValidEmail] = useState<boolean>(true);
  const [productionRole, setProductionRole] =
    useState<ConsoleAccessRole | null>(null);
  const [stagingRole, setStagingRole] = useState<ConsoleAccessRole | null>(
    null
  );
  const [developmentRole, setDevelopmentRole] =
    useState<ConsoleAccessRole | null>(null);
  const [adminRights, setAdminRights] = useState<boolean>(false);
  const [agreement, setAgreement] = useState<boolean>(selectEnabled);
  const { projectId = "", organizationId = "" } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const organization = useOrganizationFromURL(organizationId);
  const { isLoading, load: loadLoggedInMember } = useLoggedInMember(
    organization?.id || ""
  );

  const invitationStatus = useAppSelector(statusSelector);

  useEffect(() => {
    loadLoggedInMember();
  }, [loadLoggedInMember]);

  const manageAccessEnabled = useSelectorWithUrlParams(
    selectHasManagementAccess
  );

  // Those errors are the ones returned from the server once
  // the validation to create a new user has failed
  const errors = useSelector(invitationErrorsSelector);

  const accessURL = `/${organizationId}/${projectId}/-/settings/access`;
  const showPerEnvironmentPermissions = !adminRights;

  useEffect(() => {
    dispatch(clearInvitationErrors());
  }, [dispatch]);

  const handleEmailChange = e => {
    e.preventDefault();
    setEmail(e.target.value);
  };

  const handleEmailBlur = () => {
    setValidEmail(isValidEmail(email));
  };

  const handleEmailFocus = () => {
    setValidEmail(true);
  };

  const roleGetters = {
    production: productionRole,
    staging: stagingRole,
    development: developmentRole
  };

  const roleSetters = {
    production: setProductionRole,
    staging: setStagingRole,
    development: setDevelopmentRole
  };

  const handleRoleChange = (value, meta) => {
    roleSetters[meta.name](value.value);
  };

  const shouldRenderSaveCancelButtons = true;

  const existingEmail =
    existingEmails.length > 0 && existingEmails.includes(email);

  const canSubmit =
    invitationStatus !== "pending" &&
    email &&
    validEmail &&
    !existingEmail &&
    (agreement || !addProjectUserPricingAgreementEnabled) &&
    (adminRights ||
      (stagingRole && stagingRole !== "no-access") ||
      (developmentRole && developmentRole !== "no-access") ||
      (productionRole && productionRole !== "no-access"));

  const handleSave = e => {
    e.preventDefault();
    dispatch(
      addInvitationThunk({
        email,
        projectId,
        organizationId,
        adminRights,
        environmentTypes: [
          // TODO: get environmentTypesfrom somewhere else
          { type: "production", role: productionRole || "no-access" },
          { type: "development", role: developmentRole || "no-access" },
          { type: "staging", role: stagingRole || "no-access" }
        ]
      })
    ).then(action => {
      // Redirect only if the request has been successful
      // if not we want to stay in this form to show the errors
      if (action.type !== "app/invitation/create/rejected") {
        navigate("..");
      }
    });
  };

  if (isLoading) return <Loading />;

  if (!manageAccessEnabled) {
    return (
      <div>{intl.formatMessage({ id: "invitation.permisssions.missing" })}</div>
    );
  }

  return (
    <div>
      <BackLink from="access" to={accessURL} capitalized={false} />
      {errors instanceof Error && errors?.message && (
        <Sticker.Root priority="critical">
          <Sticker.Icon priority="critical" />
          <Sticker.Text>{errors?.message}</Sticker.Text>
        </Sticker.Root>
      )}
      <Typography.Title tag="h3">
        {capitalize(intl.formatMessage({ id: "add_user" }))}
      </Typography.Title>
      <S.Form>
        {organization && selectEnabled ? (
          <OrganizationMemberSelect
            organization={organization}
            onChange={email => setEmail(email || "")}
            value={email}
          />
        ) : (
          <Input
            analyticId="email"
            id="email"
            required
            label={intl.formatMessage({ id: "email" })}
            placeholder={intl.formatMessage({ id: "email" })}
            value={email || undefined}
            isValid={validEmail}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            onFocus={handleEmailFocus}
            error={
              !validEmail
                ? intl.formatMessage({ id: "enter_valid_email" })
                : existingEmail
                  ? intl.formatMessage({ id: "duplicate_email" }, { email })
                  : ""
            }
            $aria-label={intl.formatMessage({ id: "user_email" })}
          />
        )}
        <S.SectionTitle tag="h4">
          {intl.formatMessage({
            id: "access.project.permissions"
          })}
        </S.SectionTitle>
        <Checkbox
          id="adminRights"
          label={`${capitalize(intl.formatMessage({ id: "project_admin" }))}`}
          checked={adminRights}
          onCheckedChanged={checked => setAdminRights(checked)}
          error=""
          forId="adminRights"
        />
        {showPerEnvironmentPermissions && (
          <div>
            <>
              <S.TitleWrapper>
                <S.SectionTitleB tag="h4">
                  {intl.formatMessage({
                    id: "invitation.environment_types.permission"
                  })}
                </S.SectionTitleB>
                <Dialog
                  align="start"
                  description={intl.formatMessage({
                    id: "access.environment_types.description"
                  })}
                  analyticId="access.environment_types.description"
                  link={
                    <a
                      href={intl.formatMessage({
                        id: "links.documentation.users"
                      })}
                    >
                      {intl.formatMessage({ id: "learnmore" })}
                    </a>
                  }
                  trigger={
                    <IconControl>
                      <S.InfoIcon />
                    </IconControl>
                  }
                />
              </S.TitleWrapper>
              <S.EnvironmentTypesTable>
                <S.EnvironmentTableHeaders>
                  <S.EnvironmentTableHeader width="20%">
                    <Typography.Label>
                      {intl.formatMessage({
                        id: "access.environment.permissions.env_types"
                      })}
                    </Typography.Label>
                  </S.EnvironmentTableHeader>
                  <S.EnvironmentTableHeader width="35%">
                    <Typography.Label>
                      {intl.formatMessage({
                        id: "invitation.environment.type"
                      })}
                    </Typography.Label>
                  </S.EnvironmentTableHeader>
                  <S.EnvironmentTableHeader width="45%">
                    <Typography.Label>
                      {intl.formatMessage({
                        id: "invitation.table.environments"
                      })}
                    </Typography.Label>
                  </S.EnvironmentTableHeader>
                </S.EnvironmentTableHeaders>
              </S.EnvironmentTypesTable>
            </>
            <>
              {ENVIRONMENT_TYPES.map(environmentType => (
                <EnvironmentTypeRow
                  key={environmentType}
                  environmentType={environmentType}
                  value={roleGetters[environmentType]}
                  onChange={handleRoleChange}
                />
              ))}
            </>
          </div>
        )}

        {!addProjectUserPricingAgreementEnabled ? (
          <>
            <S.SshTextContainer>
              <Sticker.Root priority="normal">
                <Sticker.Icon priority="normal" />
                <Sticker.Text>
                  {intl.formatMessage({
                    id: "access.alert"
                  })}
                </Sticker.Text>
              </Sticker.Root>
            </S.SshTextContainer>
            <hr />
          </>
        ) : (
          <S.CheckboxWrapper>
            {!selectEnabled && (
              <Checkbox
                id="agreement"
                required
                label={intl.formatMessage({ id: "add_user_agreement" })}
                checked={agreement}
                onCheckedChanged={checked => setAgreement(checked)}
                forId="agreement"
              />
            )}
          </S.CheckboxWrapper>
        )}

        {shouldRenderSaveCancelButtons && (
          <ButtonWrapper>
            <Button
              analyticId="project.accesslist.add_user"
              id="access-save"
              type="submit"
              onClick={handleSave}
              disabled={!canSubmit}
            >
              {capitalize(
                intl.formatMessage({ id: "project.accesslist.add_user" })
              )}
            </Button>
            <Button
              analyticId="cancel"
              variant="secondary"
              id="access-cancel"
              to={accessURL}
            >
              {capitalize(intl.formatMessage({ id: "cancel" }))}
            </Button>
          </ButtonWrapper>
        )}
      </S.Form>
    </div>
  );
};

export interface EnvironmentTypeRowProps {
  environmentType: string;
  environment?: {
    role: string;
  };
  value?: "no-access" | "admin" | "contributor" | "viewer";
  onChange: (e: any, meta: any) => void;
}
